/**
 * What the change-order list offers per status, and how an approval reads.
 * Mirrors the database's rules (0038) so no button is shown that would only
 * be refused.
 */

import type { ChangeOrderRow } from '@/types/database';
import { fmtDate, fmtMoney } from '@/lib/dom';

export interface ChangeOrderActions {
  canSend: boolean;
  canEdit: boolean;
  /** Record an approval received by email or phone. */
  canRecordApproval: boolean;
  /** The client said no. */
  canDecline: boolean;
  /** The designer takes it back. */
  canWithdraw: boolean;
  /** Reopen an approved one for editing, keeping the approved version (0041). */
  canRevise: boolean;
  /**
   * 0046 — put a withdrawn or declined one back in play as a draft, keeping
   * its trail. NOT the same as revising: only a move out of APPROVED files a
   * revision, and reopening a change order nobody approved has nothing to file.
   */
  canReopen: boolean;
  /** 0046 — move it to Deleted. Refused by the database while approved. */
  canDelete: boolean;
}

export function changeOrderActions(
  co: Pick<ChangeOrderRow, 'status' | 'deleted_at'>,
): ChangeOrderActions {
  // In the bin, the only move is back out of it. Offering Send or Edit on a
  // deleted change order would be offering to edit something invisible.
  if (co.deleted_at) {
    return {
      canSend: false, canEdit: false, canRecordApproval: false, canDecline: false,
      canWithdraw: false, canRevise: false, canReopen: false, canDelete: false,
    };
  }
  switch (co.status) {
    case 'draft':
      return { canSend: true, canEdit: true, canRecordApproval: true, canDecline: false, canWithdraw: true, canRevise: false, canReopen: false, canDelete: true };
    case 'sent':
      return { canSend: false, canEdit: true, canRecordApproval: true, canDecline: true, canWithdraw: true, canRevise: false, canReopen: false, canDelete: true };
    case 'acknowledged':
      // Locked by the database, but no longer a dead end: revising files the
      // approved version away and reopens this one as a draft (0041). It
      // cannot be deleted while approved — the client agreed to those numbers
      // and they move the revised budget (0046). Revise it first.
      return { canSend: false, canEdit: false, canRecordApproval: false, canDecline: false, canWithdraw: false, canRevise: true, canReopen: false, canDelete: false };
    default:
      // Declined / Cancelled: closed, but reopenable as a draft (0046).
      return { canSend: false, canEdit: false, canRecordApproval: false, canDecline: false, canWithdraw: false, canRevise: false, canReopen: true, canDelete: true };
  }
}

/** Whether a change order in the bin can be taken back out. Always, so far. */
export function canRestore(co: Pick<ChangeOrderRow, 'deleted_at'>): boolean {
  return co.deleted_at != null;
}

/**
 * Split a project's change orders into the ones on the page and the ones in
 * the bin.
 *
 * Partitioned here rather than filtered in the query: the workspace loads in
 * ONE round (CLAUDE.md), and a second query for the Deleted section would add
 * a round to every project open for a section almost nobody looks at.
 */
export function partitionChangeOrders<T extends Pick<ChangeOrderRow, 'deleted_at'>>(
  cos: readonly T[],
): { live: T[]; deleted: T[] } {
  return {
    live: cos.filter((c) => !c.deleted_at),
    deleted: cos.filter((c) => c.deleted_at),
  };
}

/** Shown before binning one: what it does, and what it doesn't. */
export function deleteWarning(co: Pick<ChangeOrderRow, 'number' | 'title'>): string {
  return `Move Change Order #${co.number} to Deleted?\n\n`
    + `“${co.title}” disappears from this list and from the client’s portal. `
    + `Its history is kept and you can restore it from the Deleted section.`;
}

/** "Approved by Jane Anderson, Sep 19 · signed in the client portal". */
export function approvalLine(
  co: Pick<ChangeOrderRow, 'status' | 'acknowledged_by_name' | 'acknowledged_at' | 'approved_via' | 'approval_note'>,
): string | null {
  if (co.status !== 'acknowledged') return null;
  const who = co.acknowledged_by_name ? `Approved by ${co.acknowledged_by_name}` : 'Approved';
  const when = co.acknowledged_at ? `, ${fmtDate(co.acknowledged_at)}` : '';
  const how = co.approved_via === 'designer'
    ? ` · recorded by the designer${co.approval_note ? `: ${co.approval_note}` : ''}`
    : co.approved_via === 'portal' ? ' · signed in the client portal' : '';
  return `${who}${when}${how}`;
}

/** "+$5,000", "−$1,500", or "—" for nothing. */
export function signedMoney(cents: number): string {
  if (!cents) return '—';
  return `${cents < 0 ? '−' : '+'}${fmtMoney(Math.abs(cents))}`;
}
